import { useTraduccion } from '@tallerya/i18n';
import { PanelLateral } from '@tallerya/ui';
import {
  fraseConEliminados,
  minuscula,
  textoDeEntrada,
  type CambioDeHistorial,
  type Campos,
} from '../../componentes/historial/cambios';
import { LineaDeTiempo } from '../../componentes/historial/LineaDeTiempo';
import { useHistorialCliente } from './datos';

// El panel «Historial» de la ficha del cliente (#181), con la línea de tiempo
// común. Lo de acá es cómo se lee cada cambio:
//
// - **Los datos personales no dicen a qué cambiaron**: nombre, código, documento,
//   notas y el valor de un contacto llegan sin valor, porque la auditoría nunca los
//   guardó. Se juntan en «Cambió: nombre, notas».
// - **Los contactos se nombran por su tipo**, que es lo único que se puede decir
//   sin el valor: «Contacto agregado: teléfono».
// - **Sus activos, solo cuándo se le agregan o se le quitan** (#194), por su placa o
//   su código de hoy: «Activo quitado: P-987 001» y debajo «Pasó a: Juan Pérez». Lo
//   demás del activo, como el kilometraje, sigue en su ficha.

export function PanelHistorial({
  clienteId,
  abierto,
  onCerrar,
}: {
  clienteId: string;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const { t } = useTraduccion();
  const consulta = useHistorialCliente(clienteId, abierto);

  const tipoDeContacto: Record<string, string> = {
    phone: t('admin.clientes.formulario.telefono'),
    email: t('admin.clientes.formulario.correo'),
    address: t('admin.clientes.formulario.direccion'),
  };
  // «Contacto agregado: teléfono», o sin el tipo si ya no se sabe cuál era.
  const contacto = (que: string) => (cambio: CambioDeHistorial) => {
    const tipo = cambio.to ? tipoDeContacto[cambio.to] : undefined;
    return tipo ? t('admin.historial.conDetalle', { que, cual: minuscula(tipo) }) : que;
  };

  // «Activo agregado: P-123 456», con «Eliminado» al lado si ya no está; sin la placa
  // si el activo no tiene ni placa ni código.
  const nombrado = (que: string) => (cambio: CambioDeHistorial) =>
    cambio.to
      ? fraseConEliminados(t, 'admin.historial.conDetalle', { que }, { cual: { texto: cambio.to, eliminado: cambio.toDeleted } })
      : que;

  const campos: Campos = {
    code: { etiqueta: t('admin.clientes.formulario.codigo') },
    kind: {
      etiqueta: t('admin.clientes.formulario.tipo'),
      valor: (clase) => (clase === 'company' ? t('admin.clientes.empresa') : t('admin.clientes.persona')),
    },
    name: { etiqueta: t('admin.clientes.formulario.nombre') },
    trade_name: { etiqueta: t('admin.clientes.formulario.nombreComercial') },
    doc_type_id: {
      etiqueta: t('admin.clientes.formulario.tipoDocumento'),
      vacio: t('admin.clientes.formulario.sinDocumento'),
    },
    doc_identifier: { etiqueta: t('admin.clientes.formulario.numeroDocumento') },
    notes: { etiqueta: t('admin.clientes.formulario.notas') },
    contact_added: { frase: contacto(t('admin.historial.contacto.agregado')) },
    contact_removed: { frase: contacto(t('admin.historial.contacto.quitado')) },
    contact_edited: { frase: contacto(t('admin.historial.contacto.editado')) },
    contact_primary: { frase: contacto(t('admin.historial.contacto.principal')) },
    asset_added: { frase: nombrado(t('admin.historial.activo.agregado')) },
    asset_removed: { frase: nombrado(t('admin.historial.activo.quitado')) },
    // Va justo debajo de «Activo quitado», cuando pasó a otro cliente.
    asset_moved_to: { frase: nombrado(t('admin.historial.activo.pasoA')) },
  };

  return (
    <PanelLateral abierto={abierto} onCerrar={onCerrar} titulo={t('admin.historial.titulo')} info={t('admin.clientes.historial.info')}>
      <LineaDeTiempo consulta={consulta} texto={(entrada) => textoDeEntrada(entrada, campos, t)} />
    </PanelLateral>
  );
}
